import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Pagination,
} from '@mui/material';
import {
  ReceiptLongOutlined,
  FilterAltOutlined,
  RefreshOutlined,
  CallReceivedOutlined,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { OrderStatus, Partner } from '../../types/api';

interface SupplierOrder {
  id: number;
  partner_id: number;
  partner_name: string;
  direction: string;
  status: OrderStatus;
  item_count?: number;
  items?: Array<{
    item_id: number;
    name: string;
    units_per_carton: number;
    requested_quantity: number;
    fulfilled_quantity: number;
  }>;
  created_at: string;
  fulfilled_at?: string;
}

const statusColors: Record<OrderStatus, 'default' | 'warning' | 'success' | 'error'> = {
  [OrderStatus.CREATED]: 'default',
  [OrderStatus.PENDING]: 'default',
  [OrderStatus.PARTIAL_FULFILLED]: 'warning',
  [OrderStatus.COMPLETED]: 'success',
  [OrderStatus.CANCELLED]: 'error',
};

const statusLabels: Record<OrderStatus, string> = {
  [OrderStatus.CREATED]: 'On Hold',
  [OrderStatus.PENDING]: 'Pending',
  [OrderStatus.PARTIAL_FULFILLED]: 'Partial Fulfilled',
  [OrderStatus.COMPLETED]: 'Completed',
  [OrderStatus.CANCELLED]: 'Cancelled',
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTodayString = () => {
  const today = new Date();
  return today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
};

const SupplierOrders: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial values from URL query params
  const initialStatus = searchParams.get('status') || '';
  const initialStartDate = searchParams.get('start_date') || getTodayString();
  const initialEndDate = searchParams.get('end_date') || getTodayString();

  // Filter states
  const [selectedSupplier, setSelectedSupplier] = useState<Partner | null>(null);
  const [status, setStatus] = useState<string>(initialStatus);
  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);

  // Data states
  const [suppliers, setSuppliers] = useState<Partner[]>([]);
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [loading, setLoading] = useState(false);

  // Pagination states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Fetch suppliers on mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await api.get('/api/partners', { params: { type: 'SUPPLIER' } });
        setSuppliers(response.data.partners || []);
      } catch (err) {
        console.error('Error fetching suppliers:', err);
      }
    };
    fetchSuppliers();
  }, []);

  // Fetch orders
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit,
        partner_type: 'SUPPLIER',
      };

      if (selectedSupplier) {
        params.partner_id = selectedSupplier.id;
      }
      if (status) {
        params.status = status;
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      const response = await api.get('/api/orders', { params });
      setOrders(response.data.data || response.data.orders || []);
      setTotal(response.data.meta?.total || 0);
      const total = response.data.meta?.total || 0;
      setTotalPages(Math.ceil(total / limit) || 1);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    // Sync filters to URL
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (selectedSupplier) params.partner_id = String(selectedSupplier.id);
    setSearchParams(params, { replace: true });
    fetchOrders();
  };

  const handleReset = () => {
    setSelectedSupplier(null);
    setStatus('');
    setStartDate(getTodayString());
    setEndDate(getTodayString());
    setPage(1);
    setSearchParams({}, { replace: true });
    setTimeout(fetchOrders, 0);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ReceiptLongOutlined sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            Supplier Orders
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <FilterAltOutlined color="action" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={500}>
            Filters
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 1.5,
            mb: 1.5,
          }}
        >
          {/* Supplier Filter */}
          <Autocomplete
            options={suppliers}
            getOptionLabel={(option) => option.name}
            value={selectedSupplier}
            onChange={(_, newValue) => setSelectedSupplier(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Supplier" placeholder="All Suppliers" size="small" />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          {/* Status Filter */}
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value={OrderStatus.CREATED}>On Hold</MenuItem>
              <MenuItem value={OrderStatus.COMPLETED}>Completed</MenuItem>
              <MenuItem value={OrderStatus.CANCELLED}>Cancelled</MenuItem>
            </Select>
          </FormControl>

          {/* Start Date */}
          <TextField
            label="From Date"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {/* End Date */}
          <TextField
            label="To Date"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="contained" size="small" onClick={handleSearch} disabled={loading}>
            Search
          </Button>
          <Button variant="outlined" size="small" onClick={handleReset} startIcon={<RefreshOutlined />}>
            Reset
          </Button>
        </Box>
      </Paper>

      {/* Orders Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Supplier</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2">Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">No orders found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography 
                        variant="body2"
                        fontWeight={500} 
                        onClick={() => navigate(`/suppliers/orders/${order.id}`)}
                        sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                      >
                        #{order.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CallReceivedOutlined sx={{ fontSize: 16 }} color="secondary" />
                        <Typography variant="body2">Inward</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{order.partner_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[order.status] || order.status}
                        color={statusColors[order.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(order.created_at)}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {orders.length} of {total} orders
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SupplierOrders;
